import {
    ContactMenuOption,
    UserAvailability,
    UserType,
    CugContactType,
    UserDeviceOs,
} from '../constants/contacts-constants';
import { isSet, randUuid, ucFirst } from './functions';
import { getCompanySettings } from '../modules/settings/stores/company-settings-slice-handler';
import { getUserSettingsJson } from '../modules/settings/stores/user-settings-slice-handler';
import { ALIAS_STATUS, USER_SETTINGS_OPTIONS } from '../constants/user-settings';
import {
    closeMainAreaLayer,
    closeSlidingLayer,
    openMainAreaLayer,
    openSlidingLayer,
} from '../modules/shared/stores/sliding-layers-slice-handler';
import t from './translate';
import { addModal } from '../modules/shared/stores/portals-slice-handler';
import { getSlice } from '../modules/shared/stores/store';
import ConfigIniUtil from './config-ini-util';

/**
 * @return string
 */
export function getLoggedInMsisdn() {
    return wcfUiCfg.msisdn;
}

/**
 * Get the initials of a user.
 * @param {CugSubscriber} user
 * @return string
 */
const computeUserInitials = (user) => {
    const companySettings = getCompanySettings();
    const userSettings = getUserSettingsJson();

    if (companySettings.AllowAlias && user.Alias) {
        return user.Alias.substring(0, 2);
    }

    // try to eliminate the damn parenthesis put in names
    const getFirstLetter = (str) => {
        if (typeof str === 'undefined') {
            return;
        }
        const bad = ['(', ')'];
        let result;

        for (let n = 0; n < str.length; n++) {
            const char = str.charAt(n);

            if (!bad.includes(char)) {
                result = char;

                break;
            }
        }

        return result;
    };

    const nameData = [getFirstLetter(user.FirstName), getFirstLetter(user.LastName)];

    if (userSettings.sortContactsBy === USER_SETTINGS_OPTIONS.SORT_CONTACTS_BY.LAST_NAME) {
        nameData.reverse();
    }

    return nameData.join('');
};

/**
 * Compute the  username and initials according to company settings regarding Alias.
 * @param {CugSubscriber} user
 * @return string
 */
export const computeUserName = (user) => {
    const companySettings = getCompanySettings();
    const userSettings = getUserSettingsJson();

    const nameData = [user.FirstName, user.LastName];

    if (userSettings.sortContactsBy === USER_SETTINGS_OPTIONS.SORT_CONTACTS_BY.LAST_NAME) {
        nameData.reverse();
    }

    if (companySettings.AllowAlias && user.Alias) {
        if (companySettings.AliasStatus === ALIAS_STATUS.ALIAS_ONLY) {
            return user.Alias;
        }

        if (companySettings.AliasStatus === ALIAS_STATUS.NAME_AND_ALIAS) {
            return `${nameData.join(' ')} (${user.Alias})`;
        }
    }

    return `${nameData.join(' ')}`;
};

/**
 * Create a user like object prefilled with default values or input values if any.
 * @param {CugSubscriber|CugGetContact} userData The user data must contain at least Msisdn.
 * @return {CugSubscriber} A COMPLETE user item which will always have all properties.
 */
export function createUserStructure(userData) {
    /**
     * @type {CugSubscriber}
     */
    const subscriber = {
        CugTimestamp: isSet(userData.CugTimestamp) ? parseInt(userData.CugTimestamp, 10) : 0,
        Msisdn: userData.Msisdn,
        Fqdn: isSet(userData.Fqdn) ? userData.Fqdn : '',
        FirstName: isSet(userData.FirstName) ? userData.FirstName : userData.Msisdn,
        LastName: isSet(userData.LastName) ? userData.LastName : '',
        Alias: isSet(userData.Alias) ? userData.Alias : '',
        ContactType: isSet(userData.ContactType) ? userData.ContactType : CugContactType.DEPARTMENT_CONTACT,
        UserAvailability: isSet(userData.UserAvailability) ? userData.UserAvailability : UserAvailability.BUSY,
        Type: isSet(userData.Type) ? userData.Type : UserType.USER,
        DeviceOs: isSet(userData.DeviceOs) ? userData.DeviceOs : '',
        RobotType: isSet(userData.RobotType) ? userData.RobotType : '',
        PictureTimestamp: isSet(userData.PictureTimestamp) ? parseInt(userData.PictureTimestamp, 10) : 0,
        UserCustomStatus: isSet(userData.UserCustomStatus) ? userData.UserCustomStatus : '',
        Email: isSet(userData.Email) ? userData.Email : '',
        Position: isSet(userData.Position) ? userData.Position : '',
        IsReachable: isSet(userData.IsReachable) ? userData.IsReachable : false,
        Priority: isSet(userData.Priority) ? userData.Priority : 0,
        UserConnectedStatus: isSet(userData.UserConnectedStatus) ? userData.UserConnectedStatus : false,
        IsGuest: isSet(userData.IsGuest) ? userData.IsGuest : false,
        IsFavorite: isSet(userData.IsFavorite) ? userData.IsFavorite : false,
        OperationalStatus: isSet(userData.OperationalStatus) ? userData.OperationalStatus : 0,
        IsGroup: false,
        ContactId: userData.Msisdn,
        IsUpdating: false,
        Groups: isSet(userData.Groups) ? userData.Groups : [],
        // these are set and relevant only in the context of a user group
        IsUserGroupAdmin: isSet(userData.IsUserGroupAdmin) ? userData.IsUserGroupAdmin : false,
        IsUserGroupCreator: isSet(userData.IsUserGroupCreator) ? userData.IsUserGroupCreator : false,
    };

    // there are properties which must be inherited from the store, when the user is cached
    // Example: the Groups property which should not be overwritten by CUG updates or by a search results caching
    const cachedUser = getSlice('contactsSlice').getUser(userData.Msisdn);

    if (cachedUser !== null) {
        const inheritedProps = [
            'IsUserGroupAdmin',
            'IsUserGroupCreator',
            'Groups',
            'Fqdn',
            'IsFavorite',
            'PictureTimestamp',
        ];

        inheritedProps.forEach((prop) => {
            if (typeof userData[prop] === 'undefined' && typeof cachedUser[prop] !== 'undefined') {
                subscriber[prop] = cachedUser[prop];
            }
        });
    }

    subscriber.SearchKey = `${subscriber.FirstName} ${subscriber.LastName} ${subscriber.Msisdn}`.toLowerCase();
    subscriber.ComputedUserName = computeUserName(subscriber);
    subscriber.ComputedUserInitials = computeUserInitials(subscriber);
    subscriber.NodeFqdn = subscriber.Fqdn;

    if (userData.Msisdn === getLoggedInMsisdn()) {
        // the logged-in user is always connected
        subscriber.UserConnectedStatus = true;
    }

    if (typeof userData.GroupsDetails !== 'undefined') {
        subscriber.Groups = [];

        userData.GroupsDetails.forEach((rawGroup) => {
            subscriber.Groups.push(
                createGroupStructure({
                    GroupId: rawGroup.OrganizationGroupId,
                    Name: rawGroup.Label,
                    Priority: rawGroup.Priority,
                }),
            );
        });
    }

    return subscriber;
}

/**
 * Create a group like object prefilled with default values or input values if any.
 * @param {CugGroup} groupData The group data must contain at least GroupId and Name.
 * @return CugGroup CugSubscriber A COMPLETE group item which will always have all properties.
 */
export function createGroupStructure(groupData) {
    /**
     * @type {CugGroup}
     */
    const group = {
        CugTimestamp: isSet(groupData.CugTimestamp) ? groupData.CugTimestamp : '0',
        Priority: isSet(groupData.Priority) ? groupData.Priority : 0,
        MultiChannelLevel: isSet(groupData.MultiChannelLevel) ? groupData.MultiChannelLevel : 0,
        AvailableOnlyReception: isSet(groupData.AvailableOnlyReception) ? groupData.AvailableOnlyReception : false,
        GroupCreateChannel: isSet(groupData.GroupCreateChannel) ? groupData.GroupCreateChannel : true,
        GroupId: groupData.GroupId,
        Name: groupData.Name,
        IsFavorite: isSet(groupData.IsFavorite) ? groupData.IsFavorite : false,
        GroupAutomaticConnection: isSet(groupData.GroupAutomaticConnection)
            ? groupData.GroupAutomaticConnection
            : false,
        GroupDenyTransmission: isSet(groupData.GroupDenyTransmission) ? groupData.GroupDenyTransmission : false,
        GroupDenyCommunication: isSet(groupData.GroupDenyCommunication) ? groupData.GroupDenyCommunication : false,
        ExceptionAutomaticConnection: isSet(groupData.ExceptionAutomaticConnection)
            ? groupData.ExceptionAutomaticConnection
            : false,
        ExceptionDenyTransmission: isSet(groupData.ExceptionDenyTransmission)
            ? groupData.ExceptionDenyTransmission
            : false,
        ExternalLinkChannel: isSet(groupData.ExternalLinkChannel) ? groupData.ExternalLinkChannel : false,
        // special properties computed locally
        Type: 'group',
        IsGroup: true,
        ContactId: groupData.GroupId,
        IsUserGroup: isSet(groupData.IsUserGroup) ? groupData.IsUserGroup : false,
        IsUserGroupAdmin: isSet(groupData.IsUserGroupAdmin) ? groupData.IsUserGroupAdmin : false,
        Members: [],
        ChildGroups: [],
        IsUpdating: false,
    };

    // this seems to be some sdk thing => DenyCommunication instead of GroupDenyCommunication

    if (!group.GroupDenyCommunication && typeof groupData.DenyCommunication !== 'undefined') {
        group.GroupDenyCommunication = groupData.DenyCommunication;
    }

    if (typeof groupData.Members !== 'undefined') {
        groupData.Members.forEach((mixedEntity) => {
            if (typeof mixedEntity.GroupId !== 'undefined') {
                group.Members.push(createGroupStructure(mixedEntity));
            } else {
                group.Members.push(createUserStructure(mixedEntity));
            }
        });
    }

    if (typeof groupData.ChildGroups !== 'undefined') {
        groupData.ChildGroups.forEach((childGroup) => {
            group.ChildGroups.push(createGroupStructure(childGroup));
        });
    }

    // there are properties which must be inherited from the store, when the group is cached
    // Example: the ChildGroups property which should not be overwritten by CUG updates or by a search results caching
    const cachedGroup = getSlice('contactsSlice').getGroup(group.GroupId);

    if (cachedGroup !== null) {
        const inheritedProps = ['Members', 'ChildGroups'];
        inheritedProps.forEach((prop) => {
            if (typeof groupData[prop] === 'undefined' && typeof cachedGroup[prop] !== 'undefined') {
                group[prop] = cachedGroup[prop];
            }
        });
    }

    group.SearchKey = `${group.Name}`.toLowerCase();

    return group;
}

/**
 * Transform an SDK entity group or subscriber to a CugContact json.
 * @param {SdkContact} sdkEntity
 * @return CugContact
 */
export function sdkEntityToCugContact(sdkEntity) {
    const sdkContact = {
        ...sdkEntity.toJSON(),
    };

    // put the damn properties in upper case letter as they are everywhere, but SDK
    Object.keys(sdkContact).forEach((prop) => {
        const okProp = ucFirst(prop);
        sdkContact[okProp] = sdkContact[prop];
    });

    if (!sdkEntity.isGroup()) {
        return createUserStructure(sdkContact);
    }

    return createGroupStructure(sdkContact);
}

/**
 * Transform a mixed entity (group or subscriber) to a CugContact object.
 * @param {{UserType?:string}} mixedEntity
 * @return CugContact
 */
export function mixedEntityToCugContact(mixedEntity) {
    if (typeof mixedEntity.GroupId !== 'undefined') {
        return createGroupStructure(mixedEntity);
    }

    return createUserStructure(mixedEntity);
}

/**
 * Convert a CUG contact structure in to a contact usable by SDK.
 * @param {CugContact} contact
 * @return {SdkContact}
 */
export function cugContactToSdkEntity(contact) {
    return new webchatSDK.Contact(contact, contact.IsGroup ? 'group' : 'subscriber');
}

/**
 * Check if the two object are equal (regarding the given properties).
 * @param {CugContact|undefined} contactA
 * @param {CugContact|undefined} contactB
 * @param {string[]} ignoredProps
 */
export function contactsAreEqual(contactA, contactB, ignoredProps = []) {
    if (
        typeof contactA !== 'object' ||
        typeof contactB !== 'object' ||
        typeof contactA.ContactId === 'undefined' ||
        typeof contactB.ContactId === 'undefined'
    ) {
        return false;
    }

    let result = true;

    Object.keys(contactA).forEach((prop) => {
        if (!result) {
            return;
        }

        if (ignoredProps.includes(prop)) {
            return;
        }

        if (typeof contactB[prop] === 'undefined') {
            result = false;
            return;
        }

        result = contactA[prop] === contactB[prop];
    });

    return result;
}

/**
 * Open a Slider which has a ContactsSelector inside
 * @param {string} contactsSelectorUuid The uuid of the contacts selector who will be used.
 * It cannot be generated here because it creates cross-reference with ContactsSlice
 * @param {ContactsSelectorOptions} contactsSelectorOptions
 * @param {WcfUiSlidingLayerOptions|null} slidingLayerOptions
 * @return {string} The id of the registered slider.
 */
export function openContactsSelectorSlider(contactsSelectorUuid, contactsSelectorOptions, slidingLayerOptions = null) {
    if (!slidingLayerOptions) {
        slidingLayerOptions = {};
    }

    slidingLayerOptions = {
        ...slidingLayerOptions,
        cssClassName: 'wcf-ui-contacts-selector-layer',
    };

    // the selection sliders can contain only humans
    contactsSelectorOptions = {
        ...contactsSelectorOptions,
        multipleSelectionOpen: true,
    };

    slidingLayerOptions.componentPath = 'contacts/components/contacts-selector';
    slidingLayerOptions.componentProperties = { contactsSelectorUuid, options: contactsSelectorOptions };

    return openSlidingLayer(slidingLayerOptions);
}

export function closeContactsSelectorSlider(slidingLayerId) {
    return closeSlidingLayer(slidingLayerId);
}

/**
 * Get all possible items that can appear in a menu related with contacts, regardless where the contacts are used.
 * @param {CugContact|null} contact
 * @return {MenuOptionData[]}
 */
export function getAllContactsMenusOptionsOrdered(contact = null) {
    return [
        {
            feature: ContactMenuOption.GROUP_CONVERSATION,
            glyphFontIcon: 'stw-create-group-conversation',
            text: t('js_all_contacts_group_conversation'),
        },
        {
            feature: ContactMenuOption.BROADCAST_MESSAGE,
            glyphFontIcon: 'stw-broadcast-message',
            text: t('js_all_create_conversation_broadcast'),
        },
        {
            feature: ContactMenuOption.MESSAGE,
            glyphFontIcon: 'stw-msg-new-conversation',
            text: t('js_all_message'),
        },
        {
            feature: ContactMenuOption.VOIP_CALL,
            glyphFontIcon: 'stw-call-voip',
            text: t('js_all_call_voip'),
        },
        {
            feature: ContactMenuOption.VIDEO_CALL,
            glyphFontIcon: 'stw-call-video',
            text: t('js_all_call_video'),
        },
        {
            feature: ContactMenuOption.PTT_CALL,
            glyphFontIcon: 'stw-call-ptt',
            text: t('js_all_walkie_talkie'),
        },
        {
            feature: ContactMenuOption.CONFERENCE_CALL,
            glyphFontIcon: 'stw-conference-call',
            text: t('js_all_conference_call'),
        },
        {
            feature: ContactMenuOption.BROADCAST_CALL,
            glyphFontIcon: 'stw-broadcast-call',
            text: t('js_all_contacts_broadcast_call'),
        },
        {
            feature: ContactMenuOption.PBX_CALL,
            glyphFontIcon: 'stw-call-pbx',
            text: t('js_all_contacts_call_out_via_pbx'),
        },
        {
            feature: ContactMenuOption.RESTRICTED_ROOM,
            glyphFontIcon: 'stw-meeting-rooms',
            text: t('js_all_contacts_video_conferencing'),
        },
        {
            feature: ContactMenuOption.SHARE_SCREEN,
            glyphFontIcon: 'stw-screen-share',
            text: t('js_all_contacts_share_screen'),
        },
        {
            feature: ContactMenuOption.BROADCAST_VIDEO,
            glyphFontIcon: 'stw-video-streaming',
            text: t('js_all_contacts_broadcast_video'),
        },
        {
            feature: ContactMenuOption.JOIN_BROADCAST_VIDEO,
            glyphFontIcon: 'stw-video-streaming',
            text: t('js_all_popup-video-streaming'),
        },
        {
            feature: ContactMenuOption.ADD_TO_FAVORITES,
            glyphFontIcon: 'stw-favorite-active',
            text: contact !== null ? t('js_all_cug_v3_add_favorite') : t('mybusiness_resources_favorites_title'),
        },
        {
            feature: ContactMenuOption.REMOVE_FROM_FAVORITES,
            glyphFontIcon: 'stw-favorite-normal',
            text: t('js_all_cug_v3_remove_favorite'),
        },
        {
            feature: ContactMenuOption.ADD_TO_CONTACTS,
            glyphFontIcon: 'stw-contact-add',
            text: t('js_all_cug_v3_add_contact'),
        },
        {
            feature: ContactMenuOption.REMOVE_FROM_CONTACTS,
            glyphFontIcon: 'stw-contact-remove',
            text: t('js_all_cug_v3_remove_contact'),
        },
        {
            feature: ContactMenuOption.DETAILS,
            glyphFontIcon: contact && contact.IsGroup ? 'stw-more' : 'stw-contact-info',
            text: t('js_all_contacts_details'),
        },
        {
            feature: ContactMenuOption.MY_BUSINESS_PROCESSES_LIST,
            glyphFontIcon: 'stw-main-my-business',
            text: t('js_all_contacts_process_list'),
        },
        {
            feature: ContactMenuOption.USER_GROUP_EDIT,
            glyphFontIcon: 'stw-edit-pen',
            text: t('js_all_edit'),
        },
        {
            feature: ContactMenuOption.USER_GROUP_DELETE,
            glyphFontIcon: 'stw-delete-bin',
            text: t('js_all_delete'),
        },
        {
            feature: ContactMenuOption.USER_GROUP_CREATE,
            glyphFontIcon: 'stw-create-group',
            text: t('js_all_contacts_user_group'),
        },
        {
            feature: ContactMenuOption.TRANSFER_CALL,
            glyphFontIcon: 'stw-call-transfer',
            text: t('js_all_transfer_call'),
        },
        {
            feature: ContactMenuOption.ADD_CALL_PARTICIPANTS,
            glyphFontIcon: 'stw-add-contact',
            text: t('js_all_add_contacts'),
        },
        {
            feature: ContactMenuOption.SHARE_CHANNEL,
            glyphFontIcon: 'stw-link-share',
            text: t('js_all_share_channel_cta'),
        },
        {
            feature: ContactMenuOption.REACH_BACK_REQUEST,
            glyphFontIcon: 'stw-link-share',
            text: t('js_all_reach_back_request'),
        },
        {
            feature: ContactMenuOption.DIALER,
            glyphFontIcon: 'stw-dialer',
            text: t('js_all_dialer'),
        },
        {
            feature: ContactMenuOption.INVITE_GUEST,
            glyphFontIcon: 'stw-invite-guest',
            text: t('js_all_guest_invite_guest'),
        },
    ];
}

/**
 * Helper function which is opening a modal with the UserGroup dialog
 * @param {CugContact[]} contacts
 * @param {number} editedGroupId
 */
export function openUserGroupDialog(contacts = [], editedGroupId = 0) {
    const dialogTitle =
        editedGroupId === 0 ? t('manage_user_groups_modal_title') : t('manage_user_groups_modal_title_edit');
    const modalId = randUuid();

    addModal({
        uuid: modalId,
        componentPath: 'contacts/components/user-groups',
        componentProperties: { preselectedContacts: contacts, editedGroupId, modalId },
        dialogTitle,
        className: 'user-groups-modal',
    });
}

/**
 * Open a sliding layer which renders a ContactsSelector, and a main area layer which renders a ContactsSelectorClient linked with the selector.
 * @param {WcfUiSlidingLayerOptions} slidingLayerOptions
 * @param {ContactsSelectorOptions} selectorOptions
 * @param {ContactsSelectorClientOptions} clientOptions
 */
export function openContactsSelectorWithClientInSlidingLayer(slidingLayerOptions, selectorOptions, clientOptions) {
    const contactsSelectorUuid = randUuid();
    let slidingLayerId = null;
    let mainAreaLayerId = null;

    const originalCancelCallback = clientOptions.cancelCallback || (() => false);
    clientOptions.cancelCallback = () => {
        closeSlidingLayer(slidingLayerId);
        originalCancelCallback();
    };
    const originalPostActionCallback = clientOptions.postActionCallback || (() => false);
    clientOptions.postActionCallback = () => {
        closeSlidingLayer(slidingLayerId);
        originalPostActionCallback();
    };

    selectorOptions.multipleSelectionOpen = true;
    // the sliding layers with contacts can only contain human users
    selectorOptions.users.restrictions = { ...(selectorOptions.users.restrictions || {}), ...{ onlyHumans: true } };

    slidingLayerId = openSlidingLayer({
        title: slidingLayerOptions.title || '',
        componentPath: 'contacts/components/contacts-selector',
        componentProperties: {
            contactsSelectorUuid,
            options: selectorOptions,
        },
        onOpen: () => {
            mainAreaLayerId = openMainAreaLayer({
                componentPath: 'contacts/components/contacts-selector-client',
                componentProperties: {
                    contactsSelectorUuid,
                    options: clientOptions,
                },
            });

            if (typeof slidingLayerOptions.onOpen === 'function') {
                slidingLayerOptions.onOpen();
            }
        },
        onClose: () => {
            closeMainAreaLayer(mainAreaLayerId);

            if (typeof slidingLayerOptions.onClose === 'function') {
                slidingLayerOptions.onClose();
            }
        },
    });
}

/**
 * Get the total count from an ids list.
 * @param {ContactsIdsList} idsList
 */
export function countContactsIdsList(idsList) {
    return idsList.msisdns.length + idsList.groupsIds.length;
}

/**
 * @param {ContactsIdsList} idsList
 */
export function contactsIdsListIsEmpty(idsList) {
    return countContactsIdsList(idsList) === 0;
}

/**
 * @return {ContactsIdsList}
 */
export function createEmptyContactsIdsList() {
    return {
        msisdns: [],
        groupsIds: [],
        totalRecordsCount: 0,
    };
}

/**
 *
 * @param {ContactsIdsList} idsList
 * @return {CugContact[]}
 */
export function contactsIdsListToContactsList(idsList) {
    /**
     * @type {CugContact[]}
     */
    const result = [];

    idsList.groupsIds.forEach((groupId) => {
        const group = getSlice('contactsSlice').getGroup(groupId);

        if (group !== null) {
            result.push(group);
        }
    });

    idsList.msisdns.forEach((msisdn) => {
        const user = getSlice('contactsSlice').getUser(msisdn);

        if (user !== null) {
            result.push(user);
        }
    });

    return result;
}

/**
 * Check if a search input has no results to show at all.
 * @param {ContactsSearchInputData} searchInputData
 */
export function searchInputHasNoResults(searchInputData) {
    return (
        searchInputData &&
        !searchInputData.userIsTyping &&
        !searchInputData.requestInProgress &&
        searchInputData.keyword.length > 0 &&
        contactsIdsListIsEmpty(searchInputData.matches)
    );
}

export function contactsRecordsOnPage() {
    return parseInt(ConfigIniUtil.getParam('pagination.contacts.recordsOnPage'), 10);
}

export function getHumanDeviceOsList() {
    return [UserDeviceOs.ANDROID, UserDeviceOs.IOS, UserDeviceOs.WEBCHAT, UserDeviceOs.THREE_GPP];
}
